import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';
import AdBanner from '../../components/feature/AdBanner';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const submitAddr = formElement.action;
      
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Contato - Ativo News"
        description="Entre em contato com a equipe do Ativo News. Estamos prontos para responder suas dúvidas sobre gestão de ativos, controle patrimonial e compliance contábil."
        keywords="contato, ativo news, suporte"
        canonical="https://ativonews.com.br/contato"
      />
      <SchemaOrg type="ContactPage" data={{
        name: "Contato - Ativo News",
        description: "Entre em contato com a equipe do Ativo News"
      }} />
      
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <div className="pt-32 pb-20">
                  <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                      <h1 className="text-5xl font-bold text-[#0F172A] mb-6">Entre em Contato</h1>
                      <p className="text-xl text-[#64748B] max-w-3xl mx-auto">
                        Tem dúvidas sobre gestão de ativos, controle patrimonial ou compliance contábil? 
                        Nossa equipe de especialistas está pronta para ajudar.
                      </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-12 mb-20">
                      {/* Informações de Contato */}
                      <div className="space-y-8">
                        <div>
                          <h2 className="text-3xl font-bold text-[#0F172A] mb-6">Fale com Nossos Especialistas</h2>
                          <p className="text-[#64748B] leading-relaxed mb-8">
                            Seja para consultoria técnica, implementação de soluções ou esclarecimento de dúvidas sobre 
                            normas contábeis, estamos aqui para apoiar sua empresa.
                          </p>
                        </div>

                        <div className="space-y-6">
                          <div className="flex items-start gap-4 p-6 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                            <div className="w-12 h-12 flex items-center justify-center bg-[#1E40AF] rounded-lg flex-shrink-0">
                              <i className="ri-mail-line text-2xl text-white"></i>
                            </div>
                            <div>
                              <h3 className="font-bold text-[#0F172A] mb-1">Email</h3>
                              <p className="text-[#64748B] text-sm mb-2">Resposta em até 24 horas</p>
                              <a href="mailto:contato@ativonews.com.br" className="text-[#1E40AF] font-semibold hover:underline cursor-pointer">
                                contato@ativonews.com.br
                              </a>
                            </div>
                          </div>

                          <div className="flex items-start gap-4 p-6 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                            <div className="w-12 h-12 flex items-center justify-center bg-[#1E40AF] rounded-lg flex-shrink-0">
                              <i className="ri-phone-line text-2xl text-white"></i>
                            </div>
                            <div>
                              <h3 className="font-bold text-[#0F172A] mb-1">Telefone</h3>
                              <p className="text-[#64748B] text-sm mb-2">Seg a Sex, 9h às 18h</p>
                              <a href="tel:+551140634000" className="text-[#1E40AF] font-semibold hover:underline cursor-pointer">
                                (11) 4063-4000
                              </a>
                            </div>
                          </div>

                          <div className="flex items-start gap-4 p-6 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                            <div className="w-12 h-12 flex items-center justify-center bg-[#1E40AF] rounded-lg flex-shrink-0">
                              <i className="ri-time-line text-2xl text-white"></i>
                            </div>
                            <div>
                              <h3 className="font-bold text-[#0F172A] mb-1">Horário de Atendimento</h3>
                              <p className="text-[#64748B] text-sm">
                                Segunda a Sexta: 9h às 18h<br />
                                Sábado e Domingo: Fechado
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="pt-6">
                          <h3 className="font-bold text-[#0F172A] mb-4">Siga-nos nas Redes Sociais</h3>
                          <div className="flex items-center gap-3">
                            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#0077B5] text-white hover:opacity-80 transition-opacity cursor-pointer">
                              <i className="ri-linkedin-fill text-xl"></i>
                            </a>
                            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#1DA1F2] text-white hover:opacity-80 transition-opacity cursor-pointer">
                              <i className="ri-twitter-x-fill text-xl"></i>
                            </a>
                            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white hover:opacity-80 transition-opacity cursor-pointer">
                              <i className="ri-instagram-fill text-xl"></i>
                            </a>
                            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#FF0000] text-white hover:opacity-80 transition-opacity cursor-pointer">
                              <i className="ri-youtube-fill text-xl"></i>
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Formulário de Contato */}
                      <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-[#E2E8F0]">
                        <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Envie sua Mensagem</h2>
                        
                        <form 
                          id="contact-form"
                          data-readdy-form
                          action="https://readdy.ai/api/form/d5luvpv72m0gvhnvdhe0"
                          onSubmit={handleSubmit}
                          className="space-y-6"
                        >
                          <div>
                            <label htmlFor="name" className="block text-sm font-semibold text-[#0F172A] mb-2">
                              Nome Completo *
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent transition-all"
                              placeholder="Seu nome completo"
                            />
                          </div>

                          <div>
                            <label htmlFor="email" className="block text-sm font-semibold text-[#0F172A] mb-2">
                              Email *
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent transition-all"
                              placeholder="seu@email.com"
                            />
                          </div>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="company" className="block text-sm font-semibold text-[#0F172A] mb-2">
                                Empresa
                              </label>
                              <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent transition-all"
                                placeholder="Nome da empresa"
                              />
                            </div>

                            <div>
                              <label htmlFor="phone" className="block text-sm font-semibold text-[#0F172A] mb-2">
                                Telefone
                              </label>
                              <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                className="w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent transition-all"
                                placeholder="(11) 99999-9999"
                              />
                            </div>
                          </div>

                          <div>
                            <label htmlFor="subject" className="block text-sm font-semibold text-[#0F172A] mb-2">
                              Assunto *
                            </label>
                            <select
                              id="subject"
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent transition-all cursor-pointer"
                            >
                              <option value="">Selecione um assunto</option>
                              <option value="consultoria">Consultoria em Gestão de Ativos</option>
                              <option value="auditoria">Preparação para Auditoria</option>
                              <option value="tecnologia">Soluções Tecnológicas (RFID/Código de Barras)</option>
                              <option value="compliance">Compliance e IFRS</option>
                              <option value="inventario">Inventário Patrimonial</option>
                              <option value="treinamento">Treinamento e Capacitação</option>
                              <option value="outro">Outro Assunto</option>
                            </select>
                          </div>

                          <div>
                            <label htmlFor="message" className="block text-sm font-semibold text-[#0F172A] mb-2">
                              Mensagem *
                            </label>
                            <textarea
                              id="message"
                              name="message"
                              value={formData.message}
                              onChange={handleChange}
                              required
                              maxLength={500}
                              rows={6}
                              className="w-full px-4 py-3 bg-white border border-[#E2E8F0] rounded-lg text-sm text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#1E40AF] focus:border-transparent transition-all resize-none"
                              placeholder="Descreva sua necessidade ou dúvida..."
                            />
                            <p className="text-xs text-[#64748B] mt-2">
                              {formData.message.length}/500 caracteres
                            </p>
                          </div>

                          {submitStatus === 'success' && (
                            <div className="p-4 bg-[#ECFDF5] border border-[#059669] rounded-lg">
                              <div className="flex items-center gap-3">
                                <i className="ri-checkbox-circle-fill text-2xl text-[#059669]"></i>
                                <div>
                                  <p className="font-semibold text-[#059669]">Mensagem enviada com sucesso!</p>
                                  <p className="text-sm text-[#065F46]">Retornaremos em breve.</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {submitStatus === 'error' && (
                            <div className="p-4 bg-[#FEF2F2] border border-[#DC2626] rounded-lg">
                              <div className="flex items-center gap-3">
                                <i className="ri-error-warning-fill text-2xl text-[#DC2626]"></i>
                                <div>
                                  <p className="font-semibold text-[#DC2626]">Erro ao enviar mensagem</p>
                                  <p className="text-sm text-[#7F1D1D]">Tente novamente ou entre em contato por telefone.</p>
                                </div>
                              </div>
                            </div>
                          )}

                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full px-8 py-4 bg-[#1E40AF] text-white font-semibold rounded-lg hover:bg-[#1E3A8A] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                          >
                            {isSubmitting ? (
                              <>
                                <i className="ri-loader-4-line animate-spin"></i>
                                Enviando...
                              </>
                            ) : (
                              <>
                                <i className="ri-send-plane-fill"></i>
                                Enviar Mensagem
                              </>
                            )}
                          </button>

                          <p className="text-xs text-[#64748B] text-center">
                            Ao enviar este formulário, você concorda com nossa política de privacidade.
                          </p>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="space-y-8">
                {/* Ad Banner */}
                <AdBanner position="vertical" size="medium" />

                {/* Mapa */}
                <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-[#E2E8F0]">
                  <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Nossa Localização</h2>
                  <div className="w-full h-96 rounded-xl overflow-hidden">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.1976!2d-46.6562!3d-23.5629!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce59c8da0aa315%3A0xd59f9431f2c9776a!2sAv.%20Paulista%2C%201374%20-%20Bela%20Vista%2C%20S%C3%A3o%20Paulo%20-%20SP!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Localização do escritório"
                    />
                  </div>
                </div>

                {/* Ad Banner */}
                <AdBanner position="square" size="small" />
              </aside>
            </div>
          </div>
        </section>

        {/* Ad Banner before Footer */}
        <section className="py-6 px-6 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto">
            <AdBanner position="horizontal" size="large" />
          </div>
        </section>
      </div>
      
      <Footer />
    </>
  );
}
